import React from "react";
import {
  AnimatePresence,
  motion,
  type TargetAndTransition,
} from "motion/react";
import { COVER_MS, UNCOVER_MS, useTheme } from "../themes/ThemeProvider";
import type { ThemeId } from "../themes/types";
import "./ThemeTransition.css";

/**
 * The switch between films, in each film's own idiom.
 *
 * A crossfade is what you reach for when the transition is a technical
 * necessity. Here it is the one moment the site is explicitly *about* being
 * four films, so it should look like leaving one and arriving at another.
 *
 * The panel covers using the outgoing film's gesture and clears using the
 * incoming one, so a switch is two different motions rather than one generic
 * one — the Nostromo's screen collapses, and the Overlook's curtain goes up.
 *
 * Transforms and opacity only. Animating `clip-path` looks like the obvious
 * way to build a wipe and does not work: the browser collapses
 * `inset(0% 0% 0% 0%)` to `inset(0%)`, so Motion sees a three-value origin
 * against a four-value target, cannot interpolate, and silently freezes the
 * clip while animating everything else.
 */

type Wipe = {
  /** Off-screen before covering. */
  hidden: TargetAndTransition;
  /** Covering the page. */
  covered: TargetAndTransition;
  /** Off-screen again, clearing. */
  gone: TargetAndTransition;
  /** Anchor for scale-based wipes. */
  origin?: string;
};

const WIPES: Record<ThemeId, Wipe> = {
  /* A CRT losing vertical hold: the image collapses to a line and springs
     back out. The one gesture everyone recognises from a screen dying. */
  alien: {
    hidden: { scaleY: 0 },
    covered: { scaleY: 1 },
    gone: { scaleY: 0 },
    origin: "center center",
  },

  /* A band sweeping down the screen, the direction the rain falls. */
  matrix: {
    hidden: { y: "-100%" },
    covered: { y: "0%" },
    gone: { y: "100%" },
  },

  /* Snuffed, then relit — the theme's whole logic is that you only see what
     the match is lighting, so its transition is darkness arriving. */
  conjuring: {
    hidden: { opacity: 0 },
    covered: { opacity: 1 },
    gone: { opacity: 0 },
  },

  /* A curtain: rises from below to cover, and keeps going up to clear. */
  shining: {
    hidden: { y: "100%" },
    covered: { y: "0%" },
    gone: { y: "-100%" },
  },
};

const ThemeTransition: React.FC = () => {
  const { transition } = useTheme();

  return (
    <AnimatePresence>
      {transition && (
        <motion.div
          key="theme-wipe"
          className="theme-wipe"
          aria-hidden="true"
          style={{ transformOrigin: WIPES[transition.from].origin }}
          initial={WIPES[transition.from].hidden}
          animate={WIPES[transition.from].covered}
          /* The exit carries its own timing rather than inheriting the
             transition prop: covering should land like a cut, clearing should
             open like a reveal, so the second half runs slower. */
          exit={{
            ...WIPES[transition.to].gone,
            transition: {
              duration: UNCOVER_MS / 1000,
              ease: [0.16, 1, 0.3, 1],
            },
          }}
          transition={{
            duration: COVER_MS / 1000,
            ease: [0.65, 0, 0.35, 1],
          }}
        />
      )}
    </AnimatePresence>
  );
};

export default ThemeTransition;
